import { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { fetchExperienceById } from 'actions/ExperienceActions'
import { fetchUserReviewsByOrder } from 'actions/ReviewActions'
import { useAppDispatch, useAppSelector, shallowEqual } from 'hooks/reduxHooks'
import { isOrderReviewed, isOrderReviewsLoaded } from 'selectors/reviewSelectors'
import { SHORT_DAY_NAME_DAY_MONTH_YEAR } from 'constants/dateFormats'
import moment from 'moment'

export default function useExperienceAirportTransferPurchaseDetails(order: App.Order, isPastOrder: boolean) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const transferItem = order.transferItems[0]

  const { experience, isMarkingAsRedeemed, isReviewed, isReviewLoaded } = useAppSelector((state) => ({
    experience: state.experience.experiences[transferItem.experienceId],
    isMarkingAsRedeemed: !!state.experience.markingBookingDetailsAsRedeemed?.[transferItem.id],
    isReviewed: isOrderReviewed(state, order.id),
    isReviewLoaded: isOrderReviewsLoaded(state, order.id),
  }), shallowEqual)

  const locationName = useMemo(() => {
    if (order.transferItems.length > 1) {
      return intl.formatMessage({
        id: 'Nhvzv1UgZ9VGBulgnR0o',
        defaultMessage: 'Multiple Locations',
        description: 'Location name shown when an order contains multiple airport transfers',
      })
    }
    if (order.transferItems[0].type === 'HOTEL-TO-AIRPORT') {
      return transferItem?.airport?.name
    }
    if (order.transferItems[0].type === 'AIRPORT-TO-HOTEL') {
      return transferItem?.hotel?.name
    }

    return null
  }, [order.transferItems, transferItem, intl])

  useEffect(() => {
    dispatch(fetchExperienceById(transferItem.experienceId))

    if (isPastOrder && !isReviewLoaded) {
      dispatch(fetchUserReviewsByOrder(order.id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, transferItem.experienceId])

  const firstImage = experience?.images?.[0]

  const experienceDate = transferItem?.pickupDate && moment(transferItem.pickupDate).format(SHORT_DAY_NAME_DAY_MONTH_YEAR)
  const experienceTime = transferItem?.pickupTime

  return {
    image: firstImage,
    location: locationName,
    name: experience?.name,
    bookingNumber: transferItem.bookingNumber,
    experienceDate,
    experienceTime,
    transferItem,
    experience,
    isMarkingAsRedeemed,
    isReviewed,
    isReviewLoaded,
  }
}
